<template>
    <div class="swap-widget">
        <!-- Header -->
        <div class="swap-header">
            <div class="swap-title">Swap</div>
            <button class="settings-btn" @click="showSettings = !showSettings" title="Slippage settings">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M12 1v6m0 6v10M4.22 4.22l4.24 4.24m7.08 7.08l4.24 4.24M1 12h6m6 0h10M4.22 19.78l4.24-4.24m7.08-7.08l4.24-4.24"></path>
                </svg>
            </button>
        </div>

        <!-- Slippage Settings Panel -->
        <div class="slippage-panel" v-if="showSettings">
            <div class="slippage-label">Slippage Tolerance</div>
            <div class="slippage-options">
                <button
                    v-for="opt in slippageOptions"
                    :key="opt"
                    class="slippage-btn"
                    :class="{ active: slippage === opt }"
                    @click="slippage = opt"
                >
                    {{ opt }}%
                </button>
                <div class="slippage-custom">
                    <input
                        type="number"
                        v-model.number="customSlippage"
                        placeholder="Custom"
                        step="0.1"
                        min="0.1"
                        max="50"
                        @input="slippage = customSlippage"
                    >
                    <span>%</span>
                </div>
            </div>
        </div>

        <!-- From Token -->
        <div class="token-input-section">
            <div class="input-label-row">
                <span class="input-label">From</span>
                <button class="refresh-btn" @click="refreshAll" :class="{ spinning: isRefreshing }" title="Refresh quote and balance">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M23 4v6h-6M1 20v-6h6"></path>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                    </svg>
                </button>
            </div>
            <div class="token-input-box">
                <div class="token-selector" @click="openTokenSelector('from')">
                    <img v-if="fromToken.logo" :src="fromToken.logo" class="token-logo" alt="">
                    <span class="token-symbol">{{ fromToken.symbol }}</span>
                    <svg class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </div>
                <div class="input-wrapper">
                    <input
                        type="text"
                        v-model="fromAmount"
                        placeholder="0.00"
                        class="amount-input"
                        @input="onAmountChange"
                    >
                    <div class="usd-value">~${{ fromUsdValue }}</div>
                </div>
            </div>
            <div class="balance-row" v-if="isConnected">
                <span class="balance-label">Balance: {{ fromBalance }} {{ fromToken.symbol }}</span>
                <div class="balance-actions">
                    <button class="balance-btn" @click="setHalfBalance">Half</button>
                    <button class="balance-btn" @click="setMaxBalance">Max</button>
                </div>
            </div>
        </div>

        <!-- Swap Direction Toggle -->
        <div class="swap-direction">
            <button class="swap-toggle" @click="swapTokens">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"></path>
                </svg>
            </button>
        </div>

        <!-- To Token -->
        <div class="token-input-section">
            <div class="input-label">To</div>
            <div class="token-input-box">
                <div class="token-selector" @click="openTokenSelector('to')">
                    <img v-if="toToken.logo" :src="toToken.logo" class="token-logo" alt="">
                    <span class="token-symbol">{{ toToken.symbol }}</span>
                    <svg class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </div>
                <div class="input-wrapper">
                    <input
                        type="text"
                        :value="toAmount"
                        placeholder="0.00"
                        class="amount-input"
                        readonly
                    >
                    <div class="usd-value">~${{ toUsdValue }}</div>
                </div>
            </div>
            <div class="balance-row to-balance" v-if="isConnected">
                <span class="balance-label">Balance: {{ toBalance }} {{ toToken.symbol }}</span>
            </div>
        </div>

        <!-- Quote Info -->
        <div class="rate-info" v-if="currentQuote">
            <div class="rate-row rate-row-clickable" @click="toggleRateDirection" title="Click to reverse">
                <span class="rate-label">Rate</span>
                <span class="rate-value rate-clickable">
                    <span v-if="!rateReversed">1 {{ fromToken.symbol }} = {{ displayRate }} {{ toToken.symbol }}</span>
                    <span v-else>1 {{ toToken.symbol }} = {{ displayRateReversed }} {{ fromToken.symbol }}</span>
                    <svg class="rate-swap-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"></path>
                    </svg>
                </span>
            </div>
            <div class="rate-row">
                <span class="rate-label">Price Impact</span>
                <span class="rate-value" :class="priceImpactClass">{{ currentQuote.getPriceImpactPercent() }}%</span>
            </div>
            <div class="rate-row" v-if="currentQuote.routeLabel">
                <span class="rate-label">Route</span>
                <span class="rate-value route-label">{{ currentQuote.routeLabel }}</span>
            </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="isLoading && !currentQuote">
            <div class="loading-spinner"></div>
            <span>Finding best route...</span>
        </div>

        <!-- Action Button -->
        <button
            class="swap-action-btn"
            :class="{ disabled: !canSwap, loading: isSwapping }"
            :disabled="!canSwap || isSwapping"
            @click="executeSwap"
        >
            <span v-if="isSwapping" class="btn-loading">
                <svg class="spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                    <path d="M12 2a10 10 0 0 1 10 10"></path>
                </svg>
                {{ swapStatus }}
            </span>
            <span v-else-if="!isConnected">Connect Wallet</span>
            <span v-else-if="isLoading && !currentQuote">Finding best route...</span>
            <span v-else-if="!fromAmount || parseFloat(fromAmount) === 0">Enter an amount</span>
            <span v-else-if="insufficientBalance">Insufficient {{ fromToken.symbol }} balance</span>
            <span v-else>Swap {{ fromToken.symbol }} → {{ toToken.symbol }}</span>
        </button>

        <!-- Powered by -->
        <div class="powered-by">
            <span>Powered by</span>
            <img v-if="activeProvider?.logo" :src="activeProvider.logo" :alt="activeProvider?.displayName" class="provider-logo-small">
            <span>{{ activeProvider?.displayName || 'Multiple Aggregators' }}</span>
        </div>

        <!-- Quote Refresh Indicator -->
        <div class="refresh-indicator" v-if="currentQuote && !isSwapping">
            <span class="refresh-dot" :class="{ pulsing: isRefreshing }"></span>
            <span class="refresh-text">{{ refreshText }}</span>
        </div>

        <!-- Token Selector Modal -->
        <TokenSelectorModal
            v-model="showTokenSelector"
            :mode="tokenSelectorMode"
            :current-from-token="fromToken.mint"
            :current-to-token="toToken.mint"
            @select="handleTokenSelect"
        />

        <!-- Wallet Modal Provider -->
        <WalletModalProvider ref="walletModalProvider">
            <template #default="{ openModal }">
                <div @click="openModal" ref="walletModalTrigger" style="display:none"></div>
            </template>
        </WalletModalProvider>

        <!-- Swap Status Toast -->
        <Teleport to="body">
            <Transition name="toast-slide">
                <div v-if="toast.visible" class="swap-toast" :class="`swap-toast--${toast.type}`">
                    <div class="swap-toast__icon">
                        <svg v-if="toast.type === 'info'" class="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                            <path d="M12 2a10 10 0 0 1 10 10"></path>
                        </svg>
                        <svg v-else-if="toast.type === 'warning'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 9v4m0 4h.01M3 12a9 9 0 1 1 18 0 9 9 0 0 1-18 0z"></path>
                        </svg>
                        <svg v-else-if="toast.type === 'success'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <svg v-else-if="toast.type === 'error'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                    </div>
                    <div class="swap-toast__content">
                        <div class="swap-toast__title">{{ toast.title }}</div>
                        <div class="swap-toast__message">{{ toast.message }}</div>
                        <a v-if="toast.link" :href="toast.link" target="_blank" class="swap-toast__link">
                            View on Solscan
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                        </a>
                    </div>
                    <button class="swap-toast__close" @click="hideSwapNotification">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useWallet, WalletModalProvider } from 'solana-wallets-vue';
import { getSwapProviderManager } from '../swap/index.js';
import { Connection, PublicKey } from '@solana/web3.js';
import TokenSelectorModal from './TokenSelectorModal.vue';
import axios from 'axios';

// Common token addresses
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

export default {
    name: 'SwapWidget',

    components: {
        TokenSelectorModal,
        WalletModalProvider
    },

    props: {
        inputToken: {
            type: String,
            default: SOL_MINT
        },
        tokenSymbol: {
            type: String,
            default: 'SOL'
        },
        outputToken: {
            type: String,
            default: null
        },
        rpcEndpoint: {
            type: String,
            default: 'https://solanac-solana-c8d3.mainnet.rpcpool.com/39701859-d2f9-40e5-93bb-491f5a4791e1'
        },
        defaultProvider: {
            type: String,
            default: 'jupiter'
        },
        direction: {
            type: String,
            default: 'buy', // 'buy' = SOL -> token, 'sell' = token -> SOL
            validator: (value) => ['buy', 'sell'].includes(value)
        }
    },

    setup(props) {
        const wallet = useWallet();

        // Provider manager
        const providerManager = ref(null);
        const availableProviders = ref([]);
        const activeProviderName = ref(props.defaultProvider);
        const activeProvider = ref(null);

        // UI state
        const showSettings = ref(false);
        const slippage = ref(0.5);
        const customSlippage = ref(null);
        const slippageOptions = [0.1, 0.5, 1.0];

        // Token selector modal state
        const showTokenSelector = ref(false);
        const tokenSelectorMode = ref('from');

        // Wallet modal refs
        const walletModalProvider = ref(null);
        const walletModalTrigger = ref(null);

        // Quote refresh state
        const lastQuoteTime = ref(null);
        const refreshCountdown = ref(0);
        const isRefreshing = ref(false);
        let refreshInterval = null;

        // Token prices
        const fromTokenPrice = ref(0);
        const toTokenPrice = ref(0);

        // Token state
        const fromToken = reactive({
            symbol: 'SOL',
            logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
            mint: SOL_MINT,
            decimals: 9
        });

        // Determine the output token mint
        const toMint = props.inputToken !== SOL_MINT ? props.inputToken : USDC_MINT;

        const toToken = reactive({
            symbol: props.tokenSymbol !== 'SOL' ? props.tokenSymbol : 'USDC',
            logo: props.inputToken !== SOL_MINT
                ? null
                : 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
            mint: toMint,
            decimals: null // Will be fetched from API
        });

        // Store the page token info for direction switching (populated on mount)
        const pageToken = reactive({
            mint: props.inputToken !== SOL_MINT ? props.inputToken : null,
            symbol: props.tokenSymbol !== 'SOL' ? props.tokenSymbol : null,
            name: null,
            logo: null,
            decimals: null
        });

        // SOL token constant for quick swapping
        const solToken = {
            symbol: 'SOL',
            name: 'Solana',
            logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
            mint: SOL_MINT,
            decimals: 9
        };

        // Current trade direction
        const currentDirection = ref(props.direction);

        // Amount state
        const fromAmount = ref('');
        const toAmount = ref('0');
        const fromBalance = ref('0');
        const toBalance = ref('0');

        // Rate display
        const rateReversed = ref(false);

        // Quote state
        const currentQuote = ref(null);
        const isLoading = ref(false);
        const isSwapping = ref(false);
        const swapStatus = ref('');

        // Status state
        const status = ref('');
        const statusMessage = ref('');

        // Toast notification state
        const toast = reactive({
            visible: false,
            type: 'info', // 'info', 'warning', 'success', 'error'
            title: '',
            message: '',
            link: null,
            autoHideTimer: null
        });

        // Debounce timer
        let quoteDebounce = null;

        // Format balance/amount with appropriate precision (show significant digits)
        const formatTokenAmount = (value) => {
            if (value === 0 || value === null || value === undefined) return '0';
            if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
            if (value >= 1) return value.toPrecision(6).replace(/\.?0+$/, '');
            // For small values, show 4 significant figures
            return value.toPrecision(4).replace(/\.?0+$/, '');
        };

        // Initialize provider manager
        const initProviders = async () => {
            try {
                providerManager.value = getSwapProviderManager({
                    rpcEndpoint: props.rpcEndpoint
                });

                await providerManager.value.registerDefaults();

                // Get available providers
                availableProviders.value = providerManager.value.getAvailableProviders();

                // Set up callbacks
                providerManager.value.onQuote(handleQuoteUpdate);
                providerManager.value.onError(handleError);
                providerManager.value.onStatus(handleStatusUpdate);
                providerManager.value.onProviderChanged(handleProviderChanged);

                // Set active provider
                await providerManager.value.setActiveProvider(props.defaultProvider);
                activeProvider.value = providerManager.value.getActiveProvider()?.getInfo();

            } catch (error) {
                console.error('Failed to initialize swap providers:', error);
                statusMessage.value = 'Failed to initialize swap providers';
                status.value = 'error';
            }
        };

        // Handle quote updates
        const handleQuoteUpdate = (quotes) => {
            if (quotes && quotes.length > 0) {
                currentQuote.value = quotes[0]; // Use best quote

                // Log raw quote data for debugging
                console.log('[SwapWidget] Quote received:', {
                    rawInput: currentQuote.value.inputAmount,
                    rawOutput: currentQuote.value.outputAmount,
                    fromDecimals: fromToken.decimals,
                    toDecimals: toToken.decimals,
                    fromSymbol: fromToken.symbol,
                    toSymbol: toToken.symbol
                });

                const amounts = currentQuote.value.getHumanAmounts(
                    fromToken.decimals,
                    toToken.decimals
                );

                console.log('[SwapWidget] Calculated amounts:', amounts);

                toAmount.value = amounts.output.toFixed(6);
                isLoading.value = false;

                // Start refresh countdown when we get a quote
                startRefreshCountdown();
            }
        };

        // Handle errors
        const handleError = ({ provider, error }) => {
            console.error(`Swap provider error (${provider}):`, error);
            statusMessage.value = error.message || 'An error occurred';
            status.value = 'error';
        };

        // Handle status updates (only show errors, not quote updates)
        const handleStatusUpdate = ({ provider, status: newStatus, message }) => {
            status.value = newStatus;
            // Only update statusMessage for errors, not for quote fetching
            if (newStatus === 'error') {
                statusMessage.value = message;
            }
        };

        // Handle provider change (internal only, triggered by prop change)
        const handleProviderChanged = ({ name, provider }) => {
            activeProviderName.value = name;
            activeProvider.value = provider;
        };

        // Fetch quote (silent - no notifications)
        const fetchQuote = async () => {
            if (!providerManager.value || !fromAmount.value || parseFloat(fromAmount.value) === 0) {
                currentQuote.value = null;
                toAmount.value = '0';
                return;
            }

            isLoading.value = true;

            const amount = Math.floor(parseFloat(fromAmount.value) * Math.pow(10, fromToken.decimals));

            try {
                const params = {
                    inputMint: fromToken.mint,
                    outputMint: toToken.mint,
                    amount: amount,
                    slippageBps: Math.floor(slippage.value * 100),
                    swapMode: 'ExactIn',
                    userPublicKey: wallet.publicKey?.value?.toString() || null
                };

                // Check if active provider supports streaming
                if (providerManager.value.activeProviderSupportsStreaming()) {
                    providerManager.value.startQuoteStream(params);
                } else {
                    const quotes = await providerManager.value.getQuote(params);
                    handleQuoteUpdate(quotes);
                }
            } catch (error) {
                handleError({ provider: activeProviderName.value, error });
                isLoading.value = false;
            }
        };

        // Debounced quote fetch
        const onAmountChange = () => {
            if (quoteDebounce) {
                clearTimeout(quoteDebounce);
            }
            quoteDebounce = setTimeout(fetchQuote, 500);
        };

        // Swap tokens
        const swapTokens = () => {
            // Save current values explicitly (not shallow copy of reactive proxy)
            const tempSymbol = fromToken.symbol;
            const tempName = fromToken.name;
            const tempLogo = fromToken.logo;
            const tempMint = fromToken.mint;
            const tempDecimals = fromToken.decimals;
            const tempPrice = fromTokenPrice.value;

            // Copy toToken values to fromToken
            fromToken.symbol = toToken.symbol;
            fromToken.name = toToken.name;
            fromToken.logo = toToken.logo;
            fromToken.mint = toToken.mint;
            fromToken.decimals = toToken.decimals;
            fromTokenPrice.value = toTokenPrice.value;

            // Copy saved fromToken values to toToken
            toToken.symbol = tempSymbol;
            toToken.name = tempName;
            toToken.logo = tempLogo;
            toToken.mint = tempMint;
            toToken.decimals = tempDecimals;
            toTokenPrice.value = tempPrice;

            // Swap balances
            const tempBalance = fromBalance.value;
            fromBalance.value = toBalance.value;
            toBalance.value = tempBalance;

            // Swap amounts - use the output amount as new input
            fromAmount.value = toAmount.value !== '0' ? toAmount.value : '';
            toAmount.value = '0';
            currentQuote.value = null;

            console.log('[SwapWidget] Tokens swapped:', {
                from: { symbol: fromToken.symbol, decimals: fromToken.decimals, price: fromTokenPrice.value },
                to: { symbol: toToken.symbol, decimals: toToken.decimals, price: toTokenPrice.value }
            });

            // Refresh quote if we have an amount
            if (fromAmount.value) {
                fetchQuote();
            }

            // Update direction based on new from token
            currentDirection.value = fromToken.mint === SOL_MINT ? 'buy' : 'sell';
        };

        // Set trade direction (buy = SOL -> token, sell = token -> SOL)
        // Uses cached pageToken data for instant switching
        const setDirection = (direction) => {
            if (!pageToken.mint || direction === currentDirection.value) return;

            currentDirection.value = direction;

            // Reset amounts
            fromAmount.value = '';
            toAmount.value = '0';
            currentQuote.value = null;

            if (direction === 'buy') {
                // Buy: SOL -> page token
                Object.assign(fromToken, solToken);
                toToken.symbol = pageToken.symbol;
                toToken.name = pageToken.name;
                toToken.logo = pageToken.logo;
                toToken.mint = pageToken.mint;
                toToken.decimals = pageToken.decimals || 9;
            } else {
                // Sell: page token -> SOL
                fromToken.symbol = pageToken.symbol;
                fromToken.name = pageToken.name;
                fromToken.logo = pageToken.logo;
                fromToken.mint = pageToken.mint;
                fromToken.decimals = pageToken.decimals || 9;
                Object.assign(toToken, solToken);
            }

            console.log(`[SwapWidget] Direction set to ${direction}:`, {
                from: { symbol: fromToken.symbol, mint: fromToken.mint },
                to: { symbol: toToken.symbol, mint: toToken.mint }
            });

            // Refresh balances and prices in background (non-blocking)
            getWalletBalance();
            getToBalance();
            fetchTokenPrices();
        };

        // Show/update swap notification toast (single persistent toast)
        const showSwapNotification = (type, title, message, link = null) => {
            // Clear any existing auto-hide timer
            if (toast.autoHideTimer) {
                clearTimeout(toast.autoHideTimer);
                toast.autoHideTimer = null;
            }

            // Update toast state
            toast.type = type;
            toast.title = title;
            toast.message = message;
            toast.link = link;
            toast.visible = true;

            // Auto-hide after delay (longer for errors and success with links)
            const duration = type === 'error' ? 10000 : (link ? 8000 : 4000);
            toast.autoHideTimer = setTimeout(() => {
                hideSwapNotification();
            }, duration);
        };

        // Hide the toast
        const hideSwapNotification = () => {
            toast.visible = false;
            if (toast.autoHideTimer) {
                clearTimeout(toast.autoHideTimer);
                toast.autoHideTimer = null;
            }
        };

        // Execute swap
        const executeSwap = async () => {
            if (!wallet.connected?.value) {
                // Trigger wallet connection modal
                if (walletModalTrigger.value) {
                    walletModalTrigger.value.click();
                }
                return;
            }

            if (!currentQuote.value) {
                return;
            }

            isSwapping.value = true;
            swapStatus.value = 'Preparing...';

            // Show initial notification
            showSwapNotification('info', 'Building Transaction', 'Fetching transaction from Jupiter...');

            try {
                // Hook into status updates for swap process
                const originalStatus = providerManager.value._activeProvider?._emitStatus?.bind(providerManager.value._activeProvider);
                if (providerManager.value._activeProvider) {
                    providerManager.value._activeProvider._emitStatus = (status, message) => {
                        if (status === 'signing') {
                            swapStatus.value = 'Sign in wallet...';
                            showSwapNotification('warning', 'Signature Required', 'Please sign the transaction in your wallet');
                        } else if (status === 'sending') {
                            swapStatus.value = 'Sending...';
                            showSwapNotification('info', 'Sending Transaction', 'Broadcasting transaction to the network...');
                        } else if (status === 'confirming') {
                            swapStatus.value = 'Confirming...';
                        }
                        if (originalStatus) originalStatus(status, message);
                    };
                }

                const result = await providerManager.value.executeSwap(
                    currentQuote.value,
                    wallet
                );

                // Restore original status handler
                if (providerManager.value._activeProvider && originalStatus) {
                    providerManager.value._activeProvider._emitStatus = originalStatus;
                }

                if (result.success) {
                    swapStatus.value = 'Success!';

                    // Format amounts with appropriate precision (show significant digits, no trailing zeros)
                    const formatAmount = (amount, decimals) => {
                        const value = amount / Math.pow(10, decimals);
                        if (value === 0) return '0';
                        if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
                        if (value >= 1) return value.toPrecision(6).replace(/\.?0+$/, '');
                        // For small values, show 4 significant figures
                        return value.toPrecision(4).replace(/\.?0+$/, '');
                    };

                    const inputHuman = formatAmount(result.inputAmount, fromToken.decimals);
                    const outputHuman = formatAmount(result.outputAmount, toToken.decimals);

                    showSwapNotification(
                        'success',
                        'Swap Successful',
                        `Swapped ${inputHuman} ${fromToken.symbol} for ${outputHuman} ${toToken.symbol}`,
                        `https://solscan.io/tx/${result.signature}`
                    );

                    // Track with analytics
                    if (window.clicky) {
                        window.clicky.goal('SwapCompleted');
                    }

                    // Reset after success
                    setTimeout(() => {
                        fromAmount.value = '';
                        toAmount.value = '0';
                        currentQuote.value = null;
                        isSwapping.value = false;
                        swapStatus.value = '';
                        refreshAll();
                    }, 2000);
                }
            } catch (error) {
                console.error('Swap failed:', error);

                // Parse error for better message
                let errorMessage = error.message || 'Swap failed';
                if (errorMessage.includes('User rejected')) {
                    errorMessage = 'Transaction cancelled by user';
                } else if (errorMessage.includes('insufficient')) {
                    errorMessage = 'Insufficient balance for this swap';
                } else if (errorMessage.includes('slippage')) {
                    errorMessage = 'Slippage tolerance exceeded. Try increasing slippage.';
                }

                showSwapNotification('error', 'Swap Failed', errorMessage);

                isSwapping.value = false;
                swapStatus.value = '';
            }
        };

        // Get wallet balance for from token
        const getWalletBalance = async () => {
            if (!wallet.publicKey?.value) {
                fromBalance.value = '0';
                return;
            }

            try {
                const connection = new Connection(props.rpcEndpoint, 'confirmed');

                if (fromToken.mint === SOL_MINT) {
                    const balance = await connection.getBalance(wallet.publicKey.value);
                    fromBalance.value = formatTokenAmount(balance / 1e9);
                } else {
                    // Get token account balance
                    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
                        wallet.publicKey.value,
                        { mint: new PublicKey(fromToken.mint) }
                    );

                    if (tokenAccounts.value.length > 0) {
                        const balance = tokenAccounts.value[0].account.data.parsed.info.tokenAmount.uiAmount;
                        fromBalance.value = formatTokenAmount(balance);
                    } else {
                        fromBalance.value = '0';
                    }
                }
            } catch (error) {
                console.error('Failed to get balance:', error);
                fromBalance.value = '0';
            }
        };

        // Get wallet balance for to token
        const getToBalance = async () => {
            if (!wallet.publicKey?.value) {
                toBalance.value = '0';
                return;
            }

            try {
                const connection = new Connection(props.rpcEndpoint, 'confirmed');

                if (toToken.mint === SOL_MINT) {
                    const balance = await connection.getBalance(wallet.publicKey.value);
                    toBalance.value = formatTokenAmount(balance / 1e9);
                } else {
                    // Get token account balance
                    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
                        wallet.publicKey.value,
                        { mint: new PublicKey(toToken.mint) }
                    );

                    if (tokenAccounts.value.length > 0) {
                        const balance = tokenAccounts.value[0].account.data.parsed.info.tokenAmount.uiAmount;
                        toBalance.value = formatTokenAmount(balance);
                    } else {
                        toBalance.value = '0';
                    }
                }
            } catch (error) {
                console.error('Failed to get to balance:', error);
                toBalance.value = '0';
            }
        };

        // Refresh all - quotes and balances
        const refreshAll = async () => {
            isRefreshing.value = true;
            try {
                await Promise.all([
                    getWalletBalance(),
                    getToBalance(),
                    fromAmount.value ? fetchQuote() : Promise.resolve()
                ]);
            } finally {
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 500);
            }
        };

        // Toggle rate direction
        const toggleRateDirection = () => {
            rateReversed.value = !rateReversed.value;
        };

        // Balance helpers
        const setHalfBalance = () => {
            const half = parseFloat(fromBalance.value) / 2;
            fromAmount.value = formatTokenAmount(half);
            onAmountChange();
        };

        const setMaxBalance = () => {
            let max = parseFloat(fromBalance.value);
            // Leave some SOL for fees
            if (fromToken.mint === SOL_MINT) {
                max = Math.max(0, max - 0.01);
            }
            fromAmount.value = formatTokenAmount(max);
            onAmountChange();
        };

        // Open token selector modal
        const openTokenSelector = (type) => {
            tokenSelectorMode.value = type;
            showTokenSelector.value = true;
        };

        // Handle token selection from modal
        const handleTokenSelect = async ({ token, mode }) => {
            const targetToken = mode === 'from' ? fromToken : toToken;
            const tokenMint = token.mint || token.address;

            // If token data is incomplete, fetch full metadata from API
            if (!token.decimals || !token.symbol) {
                const metadata = await fetchTokenMetadata(tokenMint);
                if (metadata) {
                    targetToken.symbol = metadata.symbol;
                    targetToken.name = metadata.name;
                    targetToken.logo = metadata.logo;
                    targetToken.mint = metadata.mint;
                    targetToken.decimals = metadata.decimals;
                } else {
                    // Fallback to what we have
                    targetToken.symbol = token.symbol || 'Unknown';
                    targetToken.name = token.name;
                    targetToken.logo = token.logoURI;
                    targetToken.mint = tokenMint;
                    targetToken.decimals = token.decimals ?? 9;
                }
            } else {
                // Token data is complete from modal
                targetToken.symbol = token.symbol;
                targetToken.name = token.name;
                targetToken.logo = token.logoURI;
                targetToken.mint = tokenMint;
                targetToken.decimals = token.decimals;
            }

            console.log(`[SwapWidget] Token selected:`, {
                symbol: targetToken.symbol,
                mint: targetToken.mint,
                decimals: targetToken.decimals
            });

            // Fetch fresh price for the new token
            await fetchTokenPrices();

            // Refresh quote if we have an amount
            if (fromAmount.value) {
                currentQuote.value = null;
                fetchQuote();
            }
        };

        // Fetch full token metadata from API (symbol, name, logo, decimals)
        const fetchTokenMetadata = async (mint) => {
            if (!mint) {
                console.warn('[SwapWidget] fetchTokenMetadata called without mint');
                return null;
            }

            try {
                const response = await axios.get('/api/tokens/search', {
                    params: { q: mint, limit: 1, enrich: true }
                });

                if (response.data && response.data.length > 0) {
                    const tokenData = response.data[0];
                    console.log(`[SwapWidget] Fetched metadata for ${mint}:`, tokenData);
                    return {
                        symbol: tokenData.symbol,
                        name: tokenData.name,
                        logo: tokenData.logoURI,
                        decimals: tokenData.decimals ?? 9,
                        mint: tokenData.mint || tokenData.address
                    };
                }

                console.warn(`[SwapWidget] No metadata found for ${mint}`);
            } catch (error) {
                console.error('[SwapWidget] Failed to fetch token metadata:', error);
            }

            return null;
        };

        // Fetch just decimals (backwards compatibility)
        const fetchTokenDecimals = async (mint) => {
            const metadata = await fetchTokenMetadata(mint);
            return metadata?.decimals ?? 9;
        };

        // Fetch token prices for USD display
        const fetchTokenPrices = async () => {
            try {
                const mints = [fromToken.mint, toToken.mint].filter(m => m);
                if (mints.length === 0) return;

                const response = await axios.get('/api/tokens/prices', {
                    params: { mints: mints.join(',') }
                });

                const prices = response.data;
                if (prices[fromToken.mint]) {
                    fromTokenPrice.value = prices[fromToken.mint].price_usd || 0;
                }
                if (prices[toToken.mint]) {
                    toTokenPrice.value = prices[toToken.mint].price_usd || 0;
                }

                console.log('[SwapWidget] Token prices fetched:', {
                    from: { symbol: fromToken.symbol, price: fromTokenPrice.value },
                    to: { symbol: toToken.symbol, price: toTokenPrice.value }
                });
            } catch (error) {
                console.error('Failed to fetch token prices:', error);
            }
        };

        // Start quote refresh countdown
        const startRefreshCountdown = () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }

            lastQuoteTime.value = Date.now();
            refreshCountdown.value = 3; // Refresh every 3 seconds

            refreshInterval = setInterval(() => {
                refreshCountdown.value--;
                if (refreshCountdown.value <= 0) {
                    if (fromAmount.value && parseFloat(fromAmount.value) > 0) {
                        isRefreshing.value = true;
                        fetchQuote();
                        setTimeout(() => {
                            isRefreshing.value = false;
                        }, 500);
                    }
                    refreshCountdown.value = 3;
                }
            }, 1000);
        };

        // Stop refresh countdown
        const stopRefreshCountdown = () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
                refreshInterval = null;
            }
        };

        // Computed properties
        const isConnected = computed(() => wallet.connected?.value || false);

        // Only check balance if wallet is connected
        const insufficientBalance = computed(() => {
            if (!isConnected.value) return false; // Don't show insufficient if not connected
            if (!fromAmount.value) return false;
            return parseFloat(fromAmount.value) > parseFloat(fromBalance.value);
        });

        const canSwap = computed(() => {
            // Must be connected, have a quote, have an amount, have sufficient balance, and not already swapping
            return isConnected.value &&
                   currentQuote.value &&
                   fromAmount.value &&
                   parseFloat(fromAmount.value) > 0 &&
                   !insufficientBalance.value &&
                   !isSwapping.value;
        });

        const displayRate = computed(() => {
            if (!currentQuote.value) return '0';
            const rate = currentQuote.value.getRate(fromToken.decimals, toToken.decimals);
            return rate.toFixed(6);
        });

        const displayRateReversed = computed(() => {
            if (!currentQuote.value) return '0';
            const rate = currentQuote.value.getRate(fromToken.decimals, toToken.decimals);
            return rate > 0 ? (1 / rate).toFixed(6) : '0';
        });

        const fromUsdValue = computed(() => {
            const amount = parseFloat(fromAmount.value) || 0;
            if (fromTokenPrice.value && amount > 0) {
                return (amount * fromTokenPrice.value).toFixed(2);
            }
            return '0.00';
        });

        const toUsdValue = computed(() => {
            const amount = parseFloat(toAmount.value) || 0;
            if (toTokenPrice.value && amount > 0) {
                return (amount * toTokenPrice.value).toFixed(2);
            }
            return '0.00';
        });

        const refreshText = computed(() => {
            if (isRefreshing.value) return 'Refreshing...';
            if (refreshCountdown.value > 0) return `Refreshes in ${refreshCountdown.value}s`;
            return 'Live quote';
        });

        const priceImpactClass = computed(() => {
            if (!currentQuote.value) return '';
            const impact = currentQuote.value.priceImpact * 100;
            if (impact > 5) return 'impact-high';
            if (impact > 1) return 'impact-medium';
            return 'impact-low';
        });

        const statusClass = computed(() => {
            return `status-${status.value}`;
        });

        // Watch for wallet changes
        watch(() => wallet.connected?.value, (connected) => {
            if (connected) {
                getWalletBalance();
                getToBalance();
            } else {
                fromBalance.value = '0';
                toBalance.value = '0';
            }
        });

        // Watch for fromToken changes
        watch(() => fromToken.mint, () => {
            getWalletBalance();
            if (fromAmount.value) {
                fetchQuote();
            }
        });

        // Watch for toToken changes
        watch(() => toToken.mint, () => {
            getToBalance();
            if (fromAmount.value) {
                fetchQuote();
            }
        });

        // Watch for direction prop changes
        watch(() => props.direction, (newDirection) => {
            if (newDirection !== currentDirection.value) {
                setDirection(newDirection);
            }
        });

        // Event handler for direction change from external sources (e.g., mobile drawer buttons)
        const handleDirectionEvent = (event) => {
            const { direction } = event.detail;
            if (direction && direction !== currentDirection.value) {
                setDirection(direction);
            }
        };

        // Lifecycle
        onMounted(async () => {
            await initProviders();

            // Listen for external direction change events
            window.addEventListener('swap-direction-change', handleDirectionEvent);

            // Fetch full metadata for page token from API and cache it
            const pageMintToFetch = props.inputToken !== SOL_MINT ? props.inputToken : null;

            if (pageMintToFetch) {
                const metadata = await fetchTokenMetadata(pageMintToFetch);
                if (metadata) {
                    // Cache in pageToken for instant direction switching
                    pageToken.mint = metadata.mint;
                    pageToken.symbol = metadata.symbol;
                    pageToken.name = metadata.name;
                    pageToken.logo = metadata.logo;
                    pageToken.decimals = metadata.decimals;

                    // Also set toToken (default buy direction)
                    toToken.mint = metadata.mint;
                    toToken.symbol = metadata.symbol;
                    toToken.name = metadata.name;
                    toToken.logo = metadata.logo;
                    toToken.decimals = metadata.decimals;
                    console.log(`[SwapWidget] Initialized pageToken:`, pageToken);
                } else {
                    // Fallback: use props if API fails
                    pageToken.mint = pageMintToFetch;
                    pageToken.symbol = props.tokenSymbol || 'Unknown';
                    pageToken.decimals = 9;

                    toToken.mint = pageMintToFetch;
                    toToken.symbol = props.tokenSymbol || 'Unknown';
                    toToken.decimals = 9;
                    console.warn(`[SwapWidget] API fetch failed, using props for pageToken`);
                }
            }

            // SOL decimals are always 9
            if (fromToken.decimals === null) {
                fromToken.decimals = 9;
            }

            // Set initial direction if specified (now synchronous)
            if (props.direction === 'sell' && pageToken.mint) {
                setDirection('sell');
            }

            getWalletBalance();
            getToBalance();
            fetchTokenPrices();
        });

        onUnmounted(() => {
            // Remove external event listener
            window.removeEventListener('swap-direction-change', handleDirectionEvent);

            if (providerManager.value) {
                providerManager.value.stopQuoteStream();
                providerManager.value.clearCallbacks();
            }
            if (quoteDebounce) {
                clearTimeout(quoteDebounce);
            }
            stopRefreshCountdown();
        });

        return {
            // Provider state
            activeProviderName,
            activeProvider,

            // UI state
            showSettings,
            slippage,
            customSlippage,
            slippageOptions,

            // Token selector
            showTokenSelector,
            tokenSelectorMode,

            // Wallet modal
            walletModalProvider,
            walletModalTrigger,

            // Refresh state
            isRefreshing,
            refreshText,

            // Token state
            fromToken,
            toToken,
            fromAmount,
            toAmount,
            fromBalance,
            toBalance,

            // Rate display
            rateReversed,

            // Quote state
            currentQuote,
            isLoading,
            isSwapping,
            swapStatus,

            // Status
            status,
            statusMessage,
            statusClass,

            // Toast
            toast,
            hideSwapNotification,

            // Computed
            isConnected,
            insufficientBalance,
            canSwap,
            displayRate,
            displayRateReversed,
            fromUsdValue,
            toUsdValue,
            priceImpactClass,

            // Methods
            onAmountChange,
            swapTokens,
            executeSwap,
            setHalfBalance,
            setMaxBalance,
            openTokenSelector,
            handleTokenSelect,
            refreshAll,
            toggleRateDirection,
            setDirection,
            currentDirection
        };
    }
};
</script>

<style scoped>
.swap-widget {
    background: linear-gradient(180deg, #1a1a2e 0%, #16162a 100%);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 16px;
    padding: 1.25rem;
    position: relative;
    overflow: visible;
}

/* Subtle glow effect */
.swap-widget::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.5), transparent);
}

/* Header */
.swap-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
}

.swap-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.settings-btn {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 6px;
    padding: 0.3rem;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.settings-btn:hover {
    background: rgba(139, 92, 246, 0.2);
    color: #e6edf3;
}

/* Status Banner */
.status-banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    font-size: 0.75rem;
    margin-bottom: 1rem;
}

.status-streaming {
    background: rgba(16, 247, 207, 0.1);
    border: 1px solid rgba(16, 247, 207, 0.2);
    color: #10f7cf;
}

.status-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    color: #ef4444;
}

.status-fetching {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.2);
    color: #8b5cf6;
}

.status-icon {
    display: flex;
}

/* Slippage Panel */
.slippage-panel {
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: 10px;
    padding: 0.75rem;
    margin-bottom: 1rem;
}

.slippage-label {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.slippage-options {
    display: flex;
    gap: 0.5rem;
}

.slippage-btn {
    flex: 1;
    padding: 0.4rem 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: #9ca3af;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
}

.slippage-btn:hover {
    border-color: rgba(139, 92, 246, 0.5);
}

.slippage-btn.active {
    background: rgba(139, 92, 246, 0.2);
    border-color: #8b5cf6;
    color: #e6edf3;
}

.slippage-custom {
    display: flex;
    align-items: center;
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 0 0.5rem;
}

.slippage-custom input {
    width: 100%;
    background: none;
    border: none;
    color: #e6edf3;
    font-size: 0.8rem;
    padding: 0.4rem 0;
    outline: none;
}

.slippage-custom span {
    color: #9ca3af;
    font-size: 0.8rem;
}

/* Token Input Section */
.token-input-section {
    margin-bottom: 0.5rem;
}

.input-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.35rem;
}

.input-label {
    font-size: 0.7rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.refresh-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.refresh-btn:hover {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
}

.refresh-btn.spinning svg {
    animation: spin 0.8s ease-in-out;
}

.token-input-box {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: 12px;
    padding: 0.875rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    transition: border-color 0.2s;
}

.token-input-box:focus-within {
    border-color: rgba(139, 92, 246, 0.5);
}

.token-selector {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 20px;
    padding: 0.4rem 0.75rem 0.4rem 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.token-selector:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
}

.token-logo {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.token-symbol {
    font-weight: 600;
    color: #e6edf3;
    font-size: 0.9rem;
}

.chevron {
    color: #9ca3af;
}

.input-wrapper {
    flex: 1;
    text-align: right;
}

.amount-input {
    width: 100%;
    background: none;
    border: none;
    color: #e6edf3;
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 1.5rem;
    font-weight: 600;
    text-align: right;
    outline: none;
}

.amount-input::placeholder {
    color: #4b5563;
}

.usd-value {
    font-size: 0.75rem;
    color: #6b7280;
    font-family: 'JetBrains Mono', monospace;
}

.balance-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
    padding: 0 0.25rem;
}

.balance-label {
    font-size: 0.7rem;
    color: #6b7280;
}

.balance-actions {
    display: flex;
    gap: 0.35rem;
}

.balance-btn {
    padding: 0.2rem 0.5rem;
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 4px;
    color: #8b5cf6;
    font-size: 0.65rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.balance-btn:hover {
    background: rgba(139, 92, 246, 0.2);
}

.to-balance {
    justify-content: flex-start;
}

/* Swap Direction Toggle */
.swap-direction {
    display: flex;
    justify-content: center;
    margin: 0.25rem 0;
    position: relative;
}

.swap-toggle {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    border: 2px solid #1a1a2e;
    border-radius: 10px;
    padding: 0.5rem;
    color: white;
    cursor: pointer;
    transition: all 0.3s;
    z-index: 1;
}

.swap-toggle:hover {
    transform: rotate(180deg);
    box-shadow: 0 0 20px rgba(139, 92, 246, 0.4);
}

/* Rate Info */
.rate-info {
    background: rgba(139, 92, 246, 0.05);
    border-radius: 8px;
    padding: 0.75rem;
    margin: 1rem 0;
}

.rate-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
}

.rate-row + .rate-row {
    margin-top: 0.35rem;
}

.rate-row-clickable {
    cursor: pointer;
    transition: background 0.2s;
    margin: -0.25rem;
    padding: 0.25rem;
    border-radius: 4px;
}

.rate-row-clickable:hover {
    background: rgba(139, 92, 246, 0.1);
}

.rate-clickable {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.rate-swap-icon {
    opacity: 0.5;
    transition: opacity 0.2s;
}

.rate-row-clickable:hover .rate-swap-icon {
    opacity: 1;
}

.rate-label {
    color: #6b7280;
}

.rate-value {
    color: #e6edf3;
    font-family: 'JetBrains Mono', monospace;
}

.route-label {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.impact-low { color: #22c55e; }
.impact-medium { color: #f59e0b; }
.impact-high { color: #ef4444; }

/* Loading State */
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;
    color: #9ca3af;
    font-size: 0.85rem;
}

.loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(139, 92, 246, 0.3);
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

/* Action Button */
.swap-action-btn {
    width: 100%;
    padding: 1rem;
    background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;
}

.swap-action-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
}

.swap-action-btn:hover::before {
    left: 100%;
}

.swap-action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.4);
}

.swap-action-btn.disabled {
    background: #374151;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.swap-action-btn.disabled::before {
    display: none;
}

.swap-action-btn.loading {
    cursor: wait;
}

.btn-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

/* Powered by */
.powered-by {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(139, 92, 246, 0.1);
    font-size: 0.7rem;
    color: #6b7280;
}

.provider-logo-small {
    height: 14px;
    opacity: 0.6;
}

/* Animations */
@keyframes spin {
    to { transform: rotate(360deg); }
}

.spin {
    animation: spin 1s linear infinite;
}

/* Refresh Indicator */
.refresh-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-top: 0.5rem;
    font-size: 0.65rem;
    color: #6b7280;
}

.refresh-dot {
    width: 6px;
    height: 6px;
    background: #22c55e;
    border-radius: 50%;
    transition: all 0.3s;
}

.refresh-dot.pulsing {
    animation: pulse 0.5s ease-in-out;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.5);
        opacity: 0.5;
    }
}

.refresh-text {
    color: #9ca3af;
}

/* Light mode styles are in resources/scss/layouts/_compass-design.scss */
</style>
